import FTSAutomationNodeSequence from "../design/automation/FTSAutomationNodeSequence.vue";
import FTSAutomationNodeIfElse from "../design/automation/FTSAutomationNodeIfElse.vue";
import FTSAutomationNodeForEach from "../design/automation/FTSAutomationNodeForEach.vue";
import FTSAutomationNodeStart from "../design/automation/FTSAutomationNodeStart.vue";
import FTSAutomationNodeTrigger from "../design/automation/FTSAutomationNodeTrigger";

import Vue from 'vue'
import { ReleaseStage } from "./enum";

var actionSelections = [
    //create 
    //update
    //perform activity
    {
        id: "doActivity",
        name: "Perform",
        description: "Perform an activity",
        variable: true,
        form: [
            { id: "name", name: "Name", type: "contenteditable", required: true },
            { id: "moduleId", name: "Module", type: "module", required: true },
            { id: "activityId", name: "Activity", type: "activity", required: true },
            { id: "form", name: "Form", type: "form", expandable: true },
        ],
    },
    {
        id: "find",
        name: "Find",
        description: "Find an entry that meets condition",
        variable: true,
        form: [
            { id: "name", name: "Name", type: "contenteditable", required: true },
            { id: "moduleId", name: "Module", type: "module", required: true },
            { id: "condition", name: "Condition", type: "filter" },
            // automation-blocks-simplification P3 / P1: which entry when several match, and what to do when none does
            { id: "sort", name: "Order by", type: "findSort" },
            { id: "whenNone", name: "When none found", type: "whenNone" },
        ],
    },
    {
        id: "findAll",
        name: "Find all",
        description: "Find all entries that meet condition",
        variable: true,
        form: [
            { id: "name", name: "Name", type: "contenteditable", required: true },
            { id: "moduleId", name: "Module", type: "module", required: true },
            { id: "condition", name: "Condition", type: "filter" },
            // automation-blocks-simplification P1 / P3: count on the server, order, how many rows, none found
            { id: "mode", name: "Result", type: "findMode" },
            { id: "sort", name: "Order by", type: "findSort" },
            { id: "limit", name: "Limit", type: "findLimit" },
            { id: "whenNone", name: "When none found", type: "whenNone" },
        ],
    },
    {
        // automation-blocks-simplification P2: add / update / remove / add-or-update / look up rows of a table
        id: "tableRows",
        name: "Table rows",
        description: "Add, update, remove or look up rows of a table",
        variable: true,
        form: [
            { id: "name", name: "Name", type: "contenteditable", required: true },
            { id: "mode", name: "Action", type: "tableRowsMode" },
            { id: "table", name: "Table", type: "tableRowsTable" },
            { id: "where", name: "Rows where", type: "tableRowsWhere" },
            { id: "match", name: "Rows", type: "tableRowsMatch" },
            { id: "fields", name: "Set columns", type: "tableRowsFields" },
            { id: "whenNone", name: "When no row matches", type: "whenNone" },
        ],
    },
    {
        id: "getForm",
        name: "Form",
        description: "Retrieve a form for an activity",
        variable: true,
        form: [
            { id: "name", name: "Name", type: "contenteditable", required: true },
            { id: "moduleId", name: "Module", type: "module", required: true },
            { id: "activityId", name: "Activity", type: "activity", required: true },
        ],
    },
    {
        id: "getDocument",
        name: "Document",
        description: "Generate a document or report",
        variable: true,
        form: [
            { id: "name", name: "Name", type: "contenteditable", required: true },
            { id: "moduleId", name: "Module", type: "module", required: true },
            { id: "templateId", name: "Template", type: "template", required: true },
        ],
        releaseStage: ReleaseStage.ALPHA
    },
    {
        id: "getShare",
        name: "Share",
        description: "Generate a share link",
        variable: true,
        form: [
            { id: "name", name: "Name", type: "contenteditable", required: true },
            { id: "moduleId", name: "Module", type: "module", required: true },
            { id: "activityId", name: "Activity", type: "activity", required: true },
        ],
        releaseStage: ReleaseStage.ALPHA
    },
    {
        id: "syncForm",
        name: 'Synchronize',
        description: "Synchronize with form formulas and automation",
        variable: true,
        form: [
            { id: "name", name: "Name", type: "contenteditable", required: true },
            { id: "current", name: "Current Form", fieldName: 'currentForm', type: "formula", required: true },
            { id: "original", name: "Original Form", fieldName: 'originalForm', type: "formula" }
        ]
    },
    {
        id: "exception",
        name: "Error",
        description: "Show error",
        form: [{ id: "message", name: "Message", type: "formula", required: true }],
    },
    {
        id: "setVariable",
        name: "Create",
        description: "Create a new Variable",
        variable: true,
        form: [
            { id: "name", name: "Name", type: "contenteditable", required: true },
            { id: "value", fieldName: 'value', name: "Value", type: "formula" },
        ],
    },
    {
        // Next number (docs/automation-blocks-simplification-proposal.md P7): the module's next number for
        // the sequence key (empty = one counter for the module), counting from Start at. Trigger and
        // scheduled runs take it atomically; a form's own automation and a debug run only show it.
        // Pad it with TEXT: "DO" & TEXT(TODAY(), "yymm") & "-" & TEXT({{No}}, "0000").
        id: "nextNumber",
        name: "Next number",
        description: "The next running number for a sequence key",
        variable: true,
        form: [
            { id: "name", name: "Name", type: "contenteditable", required: true },
            { id: "value", fieldName: 'value', name: "Sequence key", type: "formula" },
            { id: "start", name: "Start at", type: "numberInput" },
        ],
    },
    {
        id: "assign",
        name: "Set",
        description: "Set value to a Variable",
        form: [
            { id: "variable", name: "Variable", type: "field", required: true },
            { id: "value", name: "Value", type: "formula", expandable: true },
        ],
    },
    {
        id: "logic",
        name: "Logic",
        description: "use defined logic",
        variable: true,
        form: [
            { id: "name", name: "Name", type: "contenteditable", required: true },
            { id: "logic", name: "Name", type: "logic", required: true },
            { id: "input", fieldName: 'input', name: "Input", type: "formula" },
            // Mechanism B1 (formsync-declared-triggers-proposal.md §3): declared trigger fields.
            // Declared => on FORM automations this action stops forcing every-edit sync and the
            // form round-trips only when a declared field changes. Empty => today's always-sync.
            { id: "triggerFields", name: "Sync trigger fields", type: "triggerFields" }
        ],
    },
    {
        // GNey teammate generate (teammate-automation-block-script-proposal.md §4.1 / §4.3.1).
        // In a MODULE automation it runs inline at submit time and its text lands in the output
        // variable. In a FORM automation it never runs inline: the sync returns a directive and
        // the client fills the form through /api/Activity/TeammateFill ("GNey decides" — the
        // whole form is the context, fillScope carves out fields it may never touch).
        id: "teammate",
        name: "GNey",
        description: "Ask GNey to generate content — on a form trigger it fills the form",
        variable: true,
        releaseStage: ReleaseStage.ALPHA,
        form: [
            { id: "name", name: "Name", type: "contenteditable", required: true },
            { id: "instruction", name: "Instruction", type: "textarea", required: true },
            { id: "fillScope", name: "GNey may fill", type: "fillScope" },
            { id: "seniority", name: "Seniority", type: "senioritySelect" },
            { id: "model", name: "Model", type: "text" },
            { id: "timeoutSeconds", name: "Timeout (seconds)", type: "numberInput" },
        ],
    }
];

var registerNodes = function (editor) {
    editor.registerNode(
        "start",
        FTSAutomationNodeStart,
        {
            // formula: vm.content,
            // information: vm.information,
        },
        {
            input: function (event) { },
        },
        {}
    );

    editor.registerNode(
        "ifElse",
        FTSAutomationNodeIfElse,
        {
            // formula: vm.content,
            // information: vm.information,
        },
        {
            input: function (event) { },
        },
        {}
    );

    editor.registerNode(
        "trigger",
        FTSAutomationNodeTrigger,
        {
            // formula: vm.content,
            // information: vm.information,
        },
        {
            input: function (event) { },
        },
        {}
    );

    editor.registerNode(
        "sequence",
        FTSAutomationNodeSequence,
        {
            // formula: vm.content,
            // information: vm.information,
        },
        {
            input: function (event) { },
        },
        {}
    );

    editor.registerNode(
        "forEach",
        FTSAutomationNodeForEach,
        {
            // formula: vm.content,
            // information: vm.information,
        },
        {
            input: function (event) { },
        },
        {}
    );
}

var addNode = function (editor, type, data, posX, posY) {
    switch (type) {
        case 'start':
            editor.addNode(
                type,
                0,
                1,
                posX,
                posY,
                "start",
                data,
                "start",
                "vue"
            );
            break;

        case 'ifElse':
            editor.addNode(
                type,
                1,
                2,
                posX,
                posY,
                "if-else",
                data,
                "ifElse",
                "vue"
            );
            break;

        case 'trigger':
            editor.addNode(
                type,
                0,
                1,
                posX,
                posY,
                "trigger",
                data,
                "trigger",
                "vue"
            );
            break;

        case 'sequence':
            editor.addNode(
                type,
                1,
                1,
                posX,
                posY,
                "sequence",
                data,
                "sequence",
                "vue"
            );
            break;

        case 'forEach':
            editor.addNode(
                type,
                1,
                2,
                posX,
                posY,
                "for-each",
                data,
                "forEach",
                "vue"
            );
            break;
    }
}

var computeVariables = function (diagram, form) {
    Object.entries(diagram).forEach(node => {
        if (node[1].name != 'start') {
            delete node[1].data.variables;
        }
    });

    Object.entries(diagram).forEach(node => {
        computeVariablesInternally(diagram, node[1], form);
    });
}

var computeVariablesInternally = function (diagram, node, form) {
    if (node.data && node.data.variables) {
        if (node.name == 'start') {
            var formVariable = node.data.variables.find(x => x.id == 'form');
            if (formVariable) {
                formVariable.form = form;
            }
        }

        return node.data.variables;
    }

    let result = [];

    //internal
    if (node.name == 'start') {
        return node.data.variables;
    }

    if (node.name == 'trigger') {
        var vars = computeVariablesInternally(diagram, diagram["1"], form);
        /* what a trigger can WATCH: a form field (fires on its change), the event payload, or
           `initial` (fires on form load). Initial is offered so "run when the form opens" can be
           a trigger rather than work hung off Start — Start runs on every sync, which also costs
           the form the narrower sync-trigger inference a purely trigger-driven graph gets. */
        result = vars.filter(x => x.id == 'form' || x.id == 'event' || x.id == 'initial');
    }

    if (node.inputs.input_1 && node.inputs.input_1.connections) {
        node.inputs.input_1.connections.forEach(inputNodeData => {
            let inputNode = diagram[inputNodeData.node];
            if (inputNode.name == 'trigger') {
                inputNode = diagram['1'];
            }

            var vars = computeVariablesInternally(diagram, inputNode, form);

            //list find all
            //table

            if (inputNode.name == 'forEach' && inputNode.data.item
                && !vars.find(x => x.id == inputNode.data.item)) {
                vars.push({
                    id: inputNode.data.item,
                    name: inputNode.data.item,
                    fieldName: inputNode.data.itemFieldName,
                    nodeId: inputNode.id,
                    type: inputNode.data.variableType,
                    moduleId: inputNode.data.moduleId,
                    informationId: inputNode.data.informationId
                });
            }
            // automation-blocks-simplification P8: the position chip "{item} #", counting from 1 (as the server)
            if (inputNode.name == 'forEach' && inputNode.data.item && inputNode.data.positionFieldName
                && !vars.find(x => x.id == inputNode.data.item + ' #')) {
                vars.push({
                    id: inputNode.data.item + ' #',
                    name: inputNode.data.item + ' #',
                    fieldName: inputNode.data.positionFieldName,
                    nodeId: inputNode.id,
                    type: 3,
                });
            }

            //console.log(`bfv${node.id}:${inputNode.id} ${vars.map(x => x.name)}`);
            let toAddVariables = vars;
            if(result != null) {
                toAddVariables = vars.filter(
                    item1 => !result.some(item2 => item1['id'] === item2['id'])
                  );
            }

            result = result.concat(toAddVariables);
        });
    }
    //console.log(`---${result.map(x => x.name)}`);
    Vue.set(node.data, 'variables', result);

    if (node.name == 'sequence') {
        Vue.set(node.data, 'priorVariables', result);

        if (node.data.actions) {
            let priors = node.data.actions
                .filter((x) =>
                    (x.type == 'plugin' && x.variable)
                    || (x.type != 'plugin' && actionSelections.find(y => y.id == x.type).variable))
                .map(function (x) {
                    // a count-only Find all holds a number, not the module's entries (as on the server)
                    if (x.type == 'findAll' && x.mode == 'count') {
                        return { id: x.id, fieldName: x.fieldName, name: x.name, nodeId: node.id, type: 3 };
                    }
                    // Table rows holds a row, its rows or (remove) the number removed: no module schema
                    if (x.type == 'tableRows') {
                        return {
                            id: x.id, fieldName: x.fieldName, name: x.name, nodeId: node.id,
                            type: x.mode == 'remove' ? 3 : undefined,
                            isList: x.mode == 'update' && x.match == 'all',
                        };
                    }
                    return {
                        id: x.id,
                        fieldName: x.fieldName,
                        name: x.name,
                        nodeId: node.id,
                        moduleId: x.module ? x.module : x.moduleId,
                        activityId: x.activityId,
                        informationId: x.informationId,
                        isList: x.isList || x.type == 'findAll',
                        isGroup: x.isGroup,
                        type: x.variableType
                    }
                });

            //todo: filter same
            Vue.set(node.data, 'variables', [...result, ...priors]);
        }
    }

    return node.data.variables;
}

var prepareVariables = function (information) {
    let informationPart = information.map(function (x) {
        return {
            fieldName: x.fieldName,
            type: x.type,
            name: x.displayName,
            moduleId: x.connectionId ? x.connectionId.toString() : null
        }
    })

    let systemPart = [
        {
            fieldName: 'createdDate',
            name: 'Created Date',
            system: true,
        }
    ]

    let constants = [
        // {
        //     fieldName: 'today',
        //     name: 'Today',
        //     constant: true,
        // },
        // {
        //     fieldName: 'tomorrow',
        //     name: 'Tomorrow',
        //     constant: true,
        // }
    ]

    if (variableType == 35) {
        return [...informationPart, ...constants];
    }

    return [...systemPart, ...informationPart, ...constants];
}

export default {
    registerNodes: registerNodes,
    addNode: addNode,
    computeVariables: computeVariables,
    actionSelections: actionSelections,
    prepareVariables: prepareVariables
}